/**
 * Связь HTTP-клиента с сессией (`authSession` из `0004`). Модуль-синглтон вне React:
 * HTTP-клиент ждёт подключения сессии и ответа первого `isauthorized`, берёт токен из памяти
 * и просит обновить его по 401. Сам токен здесь не хранится.
 */

export type AuthStatus = "unknown" | "authenticated" | "anonymous";

export interface AuthBridge {
    /** Разрешается после ответа первого `isauthorized` — и при `isAuthorized: false`. */
    ready(): Promise<void>;
    /** Access-токен из памяти или `null`, если сессии нет. */
    getAccessToken(): string | null;
    /** Changes when the account/session context is replaced, never on a token refresh. */
    getContextKey?(): number;
    /** Обновить токен через `isauthorized`. `true` — получен новый токен, `false` — сессии больше нет. */
    refresh(): Promise<boolean>;
}

type Listener = () => void;

function createSignal() {
    const listeners = new Set<Listener>();
    return {
        subscribe(listener: Listener): () => void {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        emit() {
            for (const listener of [...listeners]) listener();
        },
    };
}

let bridge: AuthBridge | null = null;
let resolveBridge: ((value: AuthBridge) => void) | null = null;
let bridgePromise: Promise<AuthBridge> | null = null;

function createBridgePromise(): Promise<AuthBridge> {
    return new Promise<AuthBridge>((resolve) => {
        resolveBridge = resolve;
    });
}

/**
 * Подключить сессию. До подключения авторизованные запросы ждут — это первый барьер:
 * запрос, вызванный раньше `AuthProvider`, не уйдёт без токена. Возвращает отключение (для тестов).
 */
export function connectAuth(next: AuthBridge): () => void {
    bridge = next;
    resolveBridge?.(next);
    resolveBridge = null;
    return () => {
        if (bridge !== next) return;
        bridge = null;
        bridgePromise = null;
    };
}

export function waitForAuthBridge(): Promise<AuthBridge> {
    if (bridge) return Promise.resolve(bridge);
    bridgePromise ??= createBridgePromise();
    return bridgePromise;
}

let status: AuthStatus = "unknown";
const statusSignal = createSignal();

/** Статус сессии для `enabled` запросов (`useAuthStatus`, `useAuthedQuery`). Пишет его `authSession` (`0004`). */
export function getAuthStatus(): AuthStatus {
    return status;
}

export function setAuthStatus(next: AuthStatus): void {
    if (status === next) return;
    status = next;
    statusSignal.emit();
}

export const subscribeAuthStatus = statusSignal.subscribe;

const sessionEndedSignal = createSignal();

/**
 * Сессия завершилась: refresh не помог (повторный 401) или пользователь вышел.
 * Подписчики чистят кэш (`queryClient.clear()`) и уводят на вход (`0004`).
 */
export const onSessionEnded = sessionEndedSignal.subscribe;

export function notifySessionEnded(): void {
    sessionEndedSignal.emit();
}

/** Сброс состояния модуля между тестами. */
export function resetAuthBridgeForTests(): void {
    bridge = null;
    bridgePromise = null;
    resolveBridge = null;
    status = "unknown";
}
